import { APIPath } from 'components/CapacityFactor/FileUpload/types';
import { Scenario, ScenarioList } from './scenario';
import { Country } from './authContext';
import { ConfigurationData } from './api';
import { Coworker, CostCentreContribution, ContractRange } from './coworker';

export type CountriesList = Array<Country>;

/**
 * Country code - eg. 'SE'
 */
export type OrgCountry = string;
export type OrgBusinessUnit = {
    /**
     * Code of the business unit - three digits
     */
    code: string;
    /**
     * Country code which the unit belongs to
     */
    country: OrgCountry;
    /**
     * Unique uuid for the business unit
     */
    id: number;
    /**
     * Display name of the business unit
     */
    name: string;
    /**
    * Optional, can contain the id of the parent unit, if this is a child unit.
    * If this is null, that is supposedly a sign that this is a top level unit.
    */
    parentId?: null | number;
    /**
     * Type of the three or two letter business unit, e.g. 'STO', 'CDC', 'DT'
     */
    type: string;
};
export type OrgCostCentre = {
    /**
     * Full cost centre code (e.g. MR10657010), ending with the 4 digit cost centre number commonly used (e.g. 7010)
     */
    id: string;
    /**
     * The display name of the cost centre
     */
    name: string;
    /**
     * unique id mapping the cost centre to the parent department
     */
    parentId: number;
};
export type OrgDepartment = {
    /**
     * Department code used in the unit, usually a bunch of digits
     */
    code: string;
    /**
     * unique id for the department
     */
    id: number;
    /**
     * Department Display name
     */
    name: string;
    /**
     * unique id mapping department to the division.
     */
    parentId: number;
};
export type OrgDivision = {
    /**
     * Division code used in the unit
     */
    code: string;
    /**
     * Unique id for the division
     */
    id: number;
    /**
     * Display name of the division
     */
    name: string;
    /**
     * The unique id mapping division to unit id.
     */
    parentId: number;
};

export type OrgData = {
    country: OrgCountry;
    businessUnits: Array<OrgBusinessUnit>;
    costCentres: Array<OrgCostCentre>;
    departments: Array<OrgDepartment>;
    divisions: Array<OrgDivision>;
    subDepartments: Array<OrgDepartment>;
};

export type DefaultContributionData = DefaultContributionDataHours | DefaultContributionDataRange;

/**
 * Default contribution data format for countries like CA, JP, US
 */
export type DefaultContributionDataRange = {
    data: Array<{ costCentre: string; value: number }>;
    rangeType: ContractRange['type'];
    min: null;
    max: null;
};

/**
 * Default contribution data format for countries like DK, SE
 */
export type DefaultContributionDataHours = {
    data: Array<{ costCentre: string; value: number }>;
    min: number;
    max: number;
    rangeType: null;
};

export type DefaultContributionConfig = {
    evening: Array<DefaultContributionData>;
    saturday: Array<DefaultContributionData>;
    sunday: Array<DefaultContributionData>;
};

/**
 * API response data for default contributions
 */
export type DefaultContributions = {
    country: string,
    buCode: string,
    contributions: DefaultContributionConfig
};

export type CustomContribution = {
    employeeId: string,
    contribution: {
        costCentre : string,
        evening : number,
        saturday: number,
        sunday: number,
        duration: {
            startDate: string,
            endDate: string
        };
    },
};

export type CustomContributions = Array<CustomContribution>;

export type DataContextValues = {
    config: ConfigurationData | undefined,
    coworkerData: Array<Coworker> | undefined,
    currentScenario: Scenario | undefined,
    currentCountry: string | undefined,
    currentUnit: string | undefined,
    currentUnitType: string | undefined,
    currentUnitRates: InputRates | undefined,
    currentUnitBudget: InputBudget | undefined,
    defaultContributions: DefaultContributions | undefined,
    customContributions: CustomContributions | undefined,
    isFetching: boolean,
    orgData: OrgData | undefined,
    getScenarios: (unit: string | undefined, unitType: string | undefined) => void,
    setCurrentScenario: React.Dispatch<React.SetStateAction<Scenario | undefined>>,
    scenarioList: ScenarioList | undefined,
    setScenarioList: React.Dispatch<React.SetStateAction<ScenarioList | undefined>>,
    selectCountry: (s: string) => void,
    selectScenario: (s: number) => void,
    selectUnit: (unitId: string, unitType: string) => void,
    scenarioBudget: boolean,
    setScenarioBudget: React.Dispatch<React.SetStateAction<boolean>>,
    setDateInterval: React.Dispatch<React.SetStateAction<DateInterval | undefined>>,
    workload: WorkloadData | undefined,
    getUploadedFileData: (fileCategory: APIPath) => void,
    modulationData: Modulation | undefined,
};

export type PAData = {
    coworkers: Array<Coworker> | undefined
};

export interface DateInterval {
    startDate: string,
    endDate: string
}

export interface RateEntity extends DateInterval {
    percentage: number,
}

export interface BudgetEntity extends DateInterval {
    hours: number
}
export interface CCBudgetEntity extends BudgetEntity {
    hours: number,
    costCentre: string
}

type CostCentreBudget = {
    id: string,
    data: Array<BudgetEntity>
};

export type CostCentreRates = {
    id: string,
    vacationRate: Array<RateEntity>,
    otherAbsenceRate: Array<RateEntity>,
    turnoverRate: Array<RateEntity>,
    sicknessRate: Array<RateEntity>,
};

export type CostCentreWeekRateFlat = {
    vacationRate: number,
    otherAbsenceRate: number,
    turnoverRate: number,
    sicknessRate: number,
};

export interface MinHoursConfig {
    createdAt: string,
    createdBy: string,
    updatedAt: string,
    updatedBy: string
}

export interface InputRates extends MinHoursConfig {
    country: string,
    buCode: string,
    rates: {
        costCentres: Array<CostCentreRates>
    }
}

export type InputBudget = {
    country: string,
    buCode: string,
    budget: {
        costCentres: Array<CostCentreBudget>
    }
};

export type ContractMixRanges = {
    percentage: number,
    actualTotal: number,
    coworkerTotal: number,
    minRange: number | null,
    maxRange: number | null
};

export type ContractMixData = {
    contractMix: Array<ContractMixRanges>
    isGlobal: boolean
};
interface WorkLoadContribution extends DateInterval {
    headcount: number
}

export type CCWorkload = {
    id: string,
    evening: Array<WorkLoadContribution>,
    saturday: Array<WorkLoadContribution>,
    sunday: Array<WorkLoadContribution>
};

export type WorkloadData = {
    costCentres: Array<CCWorkload>
};

export type Contribution = {
    personId: string,
    contributions : Array<CostCentreContribution>
};

export type AuthorizeBU = {
    businessUnits: string[];
};

export type ContractMixInput = {
    filters: {
        departments: Array<string>,
        divisions: Array<string>,
        costCentres: Array<string>
    }
};

export type FileUploadSuccess = {
    userAction: boolean,
    fileName: string,
    isUpdatedAt: string,
    isUpdatedBy: string
};
export type FileUploadErrors = {
    sheetName: string,
    row: number,
    col: number,
    error: string
};

export type FileUploadResponse = {
    createdAt: string | null;
    createdBy: string | null;
    errorText: string;
    errors: Array<FileUploadErrors>;
    fileName: string;
    module: string;
    updatedAt: string | null;
    updatedBy: string | null;
};

export type FileUploadFailed = {
    category: string;
    fileName: string;
    errorText: string;
    errors: Array<FileUploadErrors> | null;
};

export enum ModulationSource { SCHEDULE = 'schedule', PLANNED = 'planned' }

export type Modulation = {
    scheduledWeeks: number
    result: Array<ModulationWeekData>,
};

export type ModulationWeekData = {
    startDate: string,
    endDate: string,
    weekNumber: number,
    scheduled: Array<ModulationDelta>,
    planned: Array<ModulationDelta>
};

export type ModulationDelta = {
    employeeId: string,
    startDate: string,
    endDate: string,
    modulationDelta: number,
    overtimeHours: number,
    absenceHours: number,
    scheduleHours: number,
    source: ModulationSource
};
